// src/utils/ReportTemplate.js
import { jsPDF } from 'jspdf';

/**
 * A utility class for generating standardized medical report PDFs
 * for Medpredix.com platform
 */
class ReportTemplate {
  /**
   * Generate a PDF report for a medical analysis
   * @param {Object} options - Configuration options for the report
   * @param {string} options.reportType - Type of report ('xray' or 'mri')
   * @param {string} options.patientId - Patient ID or label
   * @param {string} options.finding - Primary finding (e.g., 'Pneumonia detected', 'glioma')
   * @param {number} options.confidence - Confidence level (0-100)
   * @param {Object} options.probabilities - Object containing probability distributions
   * @param {string} options.description - Clinical description
   * @param {Array<string>} options.recommendations - Array of recommendation strings
   * @param {HTMLImageElement} options.imageRef - Reference to the image to include
   * @param {string} options.imageUrl - URL of the image
   * @param {Function} options.onComplete - Callback function after PDF is generated
   */
  static generateReport(options) {
    const {
      reportType = 'xray',
      patientId = 'Unknown',
      finding = '',
      confidence = 0,
      probabilities = {},
      description = '',
      recommendations = [],
      imageRef = null,
      imageUrl = null,
      onComplete = null
    } = options;

    // Create new PDF document
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    // Define standard medical report template
    this._addReportHeader(doc, reportType, finding);
    
    let yPos = this._addMetadataSection(doc, patientId, reportType);
    
    yPos = this._addClinicalFindings(doc, finding, confidence, yPos);
    
    yPos = this._addClinicalDescription(doc, description, yPos);

    if (imageRef && imageUrl) {
      // Handle image asynchronously
      this._addImage(doc, imageRef, imageUrl, yPos, (newYPos) => {
        let updatedYPos = newYPos;
        
        // Continue with the rest of the report
        updatedYPos = this._addProbabilitiesTable(doc, probabilities, reportType, updatedYPos);
        
        updatedYPos = this._addRecommendations(doc, recommendations, updatedYPos);
        
        this._addFooter(doc, pageHeight);
        
        
        // Save or return the document
        if (onComplete) {
          onComplete(doc);
        } else {
          const filename = reportType === 'xray' 
            ? 'Medpredix_Chest_Xray_Report.pdf' 
            : 'Medpredix_Brain_MRI_Report.pdf';
          doc.save(filename);
        }
      });
    } else {
      // No image, continue with the report
      yPos = this._addProbabilitiesTable(doc, probabilities, reportType, yPos);
      
      yPos = this._addRecommendations(doc, recommendations, yPos);
      
      this._addFooter(doc, pageHeight);
      
      this._addWatermark(doc, 'AI-Generated Report');
      
      // Save or return the document
      if (onComplete) {
        onComplete(doc);
      } else {
        const filename = reportType === 'xray' 
          ? 'Medpredix_Chest_Xray_Report.pdf' 
          : 'Medpredix_Brain_MRI_Report.pdf';
        doc.save(filename);
      }
    }
  }

  /**
   * Add the report header with logo and title
   * @private
   */
  static _addReportHeader(doc, reportType, finding) {
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    
    // Define color schemes based on report type and finding
    let mainColor, accentColor;
    
    if (reportType === 'xray') {
      mainColor = finding.toLowerCase().includes('pneumonia') 
        ? [220, 53, 69] // Red for pneumonia
        : [40, 167, 69]; // Green for normal
    } else {
      // MRI color scheme
      if (finding === 'glioma') {
        mainColor = [255, 99, 132]; // Red
      } else if (finding === 'meningioma') {
        mainColor = [255, 159, 64]; // Orange
      } else if (finding === 'pituitary') {
        mainColor = [255, 205, 86]; // Yellow
      } else {
        mainColor = [75, 192, 192]; // Teal for no tumor
      }
    }
    
    accentColor = mainColor;
    
    // Add header background with subtle gradient
    doc.setFillColor(245, 245, 250);
    doc.rect(0, 0, pageWidth, 40, 'F');
    
    // Add accent color line
    doc.setFillColor(accentColor[0], accentColor[1], accentColor[2], 1);
    doc.rect(0, 40, pageWidth, 3, 'F');
    
    // Add logo placeholder
    doc.setDrawColor(200, 200, 200);
    doc.setFillColor(250, 250, 250);
    doc.roundedRect(margin, 10, 40, 20, 1, 1, 'FD');
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(100, 100, 100);
    doc.text('MEDPREDIX', margin + 6, 22);
    doc.setFontSize(8);
    doc.text('.com', margin + 34, 22);
    
    // Add report title
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.setTextColor(60, 60, 60);
    
    const reportTitle = reportType === 'xray' 
      ? 'CHEST X-RAY ANALYSIS REPORT' 
      : 'BRAIN MRI ANALYSIS REPORT';
    
    doc.text(reportTitle, pageWidth / 2, 25, { align: 'center' });
    
    // Add subtle design element
    doc.setDrawColor(230, 230, 230);
    doc.setLineWidth(0.5);
    
    // Draw horizontal line beneath header
    doc.line(margin, 45, pageWidth - margin, 45);
  }

  /**
   * Add the metadata section with patient and report details
   * @private
   */
  static _addMetadataSection(doc, patientId, reportType) {
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    const yPos = 60;
    
    // Document identifier section
    doc.setFillColor(248, 249, 250);
    doc.roundedRect(margin, yPos, pageWidth - (margin * 2), 35, 2, 2, 'F');
    
    // Left column - Report details
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(80, 80, 80);
    
    doc.text('Report ID:', margin + 10, yPos + 12);
    doc.text('Patient ID:', margin + 10, yPos + 22);
    doc.text('Report Date:', margin + 10, yPos + 32);
    
    // Generate a unique report ID based on timestamp
    const reportId = reportType.toUpperCase() + '-' + new Date().getTime().toString().slice(-8);
    const reportDate = new Date().toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
    
    doc.setFont('helvetica', 'normal');
    doc.text(reportId, margin + 60, yPos + 12);
    doc.text(patientId, margin + 60, yPos + 22);
    doc.text(reportDate, margin + 60, yPos + 32);
    
    // Right column - Institution details
    doc.setFont('helvetica', 'bold');
    doc.text('Institution:', pageWidth - margin - 80, yPos + 12);
    doc.text('Analysis Type:', pageWidth - margin - 80, yPos + 22);
    doc.text('Provider:', pageWidth - margin - 80, yPos + 32);
    
    doc.setFont('helvetica', 'normal');
    doc.text('Medpredix Medical Imaging', pageWidth - margin - 10, yPos + 12, { align: 'right' });
    doc.text(reportType === 'xray' ? 'Chest X-Ray Analysis' : 'Brain MRI Analysis', pageWidth - margin - 10, yPos + 22, { align: 'right' });
    doc.text('AI-Assisted Diagnosis', pageWidth - margin - 10, yPos + 32, { align: 'right' });
    
    return yPos + 45; // Return the next Y position
  }

  /**
   * Add the clinical findings section
   * @private
   */
  static _addClinicalFindings(doc, finding, confidence, startY) {
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    let yPos = startY;
    
    // Section title
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(70, 70, 70);
    doc.text('CLINICAL FINDINGS', margin, yPos);
    
    yPos += 8;
    
    // Create a box for the findings
    let findingColor;
    
    // Set color based on finding
    if (finding === 'Pneumonia detected' || finding === 'glioma') {
      findingColor = [220, 53, 69]; // Red for severe findings
    } else if (finding === 'meningioma' || finding === 'pituitary') {
      findingColor = [255, 193, 7]; // Yellow/amber for concerning findings
    } else {
      findingColor = [40, 167, 69]; // Green for normal/no tumor
    }
    
    // Finding box with soft background
    doc.setFillColor(findingColor[0], findingColor[1], findingColor[2], 0.1);
    doc.roundedRect(margin, yPos, pageWidth - (margin * 2), 30, 2, 2, 'F');
    
    // Add border accent
    doc.setDrawColor(findingColor[0], findingColor[1], findingColor[2], 0.5);
    doc.setLineWidth(0.5);
    doc.roundedRect(margin, yPos, pageWidth - (margin * 2), 30, 2, 2, 'D');
    
    // Display the finding
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(findingColor[0], findingColor[1], findingColor[2]);
    
    // Format finding text properly
    let formattedFinding = finding;
    if (typeof finding === 'string') {
      // Capitalize first letter of each word for tumor types
      if (['glioma', 'meningioma', 'pituitary', 'no tumor'].includes(finding.toLowerCase())) {
        formattedFinding = finding.split(' ').map(word => 
          word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' ');
      }
    }
    
    doc.text('Finding:', margin + 10, yPos + 12);
    doc.text(formattedFinding, margin + 50, yPos + 12);
    
    // Display confidence score
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(80, 80, 80);
    doc.text('Confidence Score:', margin + 10, yPos + 22);
    
    // Add confidence value with visual indicator
    doc.text(`${confidence.toFixed(1)}%`, margin + 80, yPos + 22);
    
    return yPos + 40; // Return the next Y position
  }

  /**
   * Add the clinical description section
   * @private
   */
  static _addClinicalDescription(doc, description, startY) {
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    let yPos = startY;
    
    // Section title
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(70, 70, 70);
    doc.text('CLINICAL DESCRIPTION', margin, yPos);
    
    yPos += 8;
    
    // Create a subtle box for the description
    doc.setFillColor(250, 250, 250);
    doc.setDrawColor(230, 230, 230);
    doc.roundedRect(margin, yPos, pageWidth - (margin * 2), 40, 2, 2, 'FD');
    
    // Add description text
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(80, 80, 80);
    
    // Format and wrap the description text
    const splitDescription = doc.splitTextToSize(description, pageWidth - (margin * 2) - 20);
    doc.text(splitDescription, margin + 10, yPos + 12);
    
    // Adjust yPos based on text height
    const textHeight = splitDescription.length * 5;
    
    return yPos + Math.max(textHeight + 15, 40); // Return the next Y position
  }

  /**
   * Add the image to the report
   * @private
   */
  static _addImage(doc, imageRef, imageUrl, startY, callback) {
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    let yPos = startY;
    
    // Check if we need a new page
    if (yPos > doc.internal.pageSize.getHeight() - 120) {
      doc.addPage();
      yPos = 20;
    }
    
    // Section title
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(70, 70, 70);
    doc.text('DIAGNOSTIC IMAGE', margin, yPos);
    
    yPos += 8;
    
    // Create image box with subtle border
    const imgBoxWidth = pageWidth - (margin * 2);
    const imgBoxHeight = 160;
    
    doc.setFillColor(250, 250, 250);
    doc.setDrawColor(230, 230, 230);
    doc.roundedRect(margin, yPos, imgBoxWidth, imgBoxHeight, 2, 2, 'FD');
    
    // Process and add the image
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.src = imageUrl;
    
    img.onload = () => {
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      ctx.drawImage(img, 0, 0);
      const imgData = canvas.toDataURL('image/png');
      
      // Calculate dimensions to fit in the box while preserving aspect ratio
      const imgWidth = 140;
      const imgHeight = (img.naturalHeight / img.naturalWidth) * imgWidth;
      
      // Center the image in the box
      const xPos = margin + (imgBoxWidth - imgWidth) / 2;
      const yPos2 = yPos + (imgBoxHeight - imgHeight) / 2;
      
      // Add image with subtle border
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.5);
      doc.addImage(imgData, 'PNG', xPos, yPos2, imgWidth, imgHeight);
      
      // Continue with the callback
      callback(yPos + imgBoxHeight + 10);
    };
  }

  /**
   * Add the probability distribution table
   * @private
   */
  static _addProbabilitiesTable(doc, probabilities, reportType, startY) {
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    let yPos = startY;
    
    // Check if we need a new page
    if (yPos > doc.internal.pageSize.getHeight() - 100) {
      doc.addPage();
      yPos = 20;
    }
    
    // Section title
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(70, 70, 70);
    doc.text('PROBABILITY ANALYSIS', margin, yPos);
    
    yPos += 10;
    
    // Create a clean table for the probabilities
    let tableData;
    
    if (reportType === 'xray') {
      // For x-ray, we typically have just pneumonia vs normal
      const pneumoniaProb = probabilities.pneumonia ? probabilities.pneumonia * 100 : 
                           (probabilities.Pneumonia ? probabilities.Pneumonia * 100 : 0);
      const normalProb = probabilities.normal ? probabilities.normal * 100 : 
                        (probabilities.Normal ? probabilities.Normal * 100 : 0);
      
      tableData = [
        { label: 'Pneumonia', value: pneumoniaProb || 0, color: [220, 53, 69] },
        { label: 'Normal', value: normalProb || 100 - pneumoniaProb, color: [40, 167, 69] }
      ];
    } else {
      // For MRI, we have multiple tumor types
      const hasData = Object.keys(probabilities).length > 0;
      
      if (hasData) {
        tableData = Object.entries(probabilities).map(([key, value]) => {
          let color;
          const lowerKey = key.toLowerCase();
          
          if (lowerKey === 'glioma') {
            color = [255, 99, 132]; // Red
          } else if (lowerKey === 'meningioma') {
            color = [255, 159, 64]; // Orange
          } else if (lowerKey === 'pituitary') {
            color = [255, 205, 86]; // Yellow
          } else {
            color = [75, 192, 192]; // Teal for no tumor
          }
          
          // Capitalize first letter
          const label = key.charAt(0).toUpperCase() + key.slice(1);
          
          return {
            label,
            value: value * 100,
            color
          };
        });
      } else {
        // Fallback if no probabilities provided
        tableData = [
          { label: 'No probability data available', value: 0, color: [150, 150, 150] }
        ];
      }
    }
    
    // Sort by probability (descending)
    tableData.sort((a, b) => b.value - a.value);
    
    // Draw table header
    doc.setFillColor(248, 249, 250);
    doc.rect(margin, yPos, pageWidth - (margin * 2), 10, 'F');
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(70, 70, 70);
    doc.text('Classification', margin + 10, yPos + 7);
    doc.text('Probability', pageWidth - margin - 40, yPos + 7);
    
    yPos += 15;
    
    // Draw probability bars
    const barWidth = pageWidth - (margin * 2) - 100;
    const barHeight = 12;
    const barSpacing = 20;
    
    tableData.forEach((item, index) => {
      // Row background (alternating)
      if (index % 2 === 0) {
        doc.setFillColor(252, 252, 252);
        doc.rect(margin, yPos - 7, pageWidth - (margin * 2), barSpacing, 'F');
      }
      
      // Label
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(70, 70, 70);
      doc.text(item.label, margin + 10, yPos);
      
      // Bar background
      doc.setFillColor(230, 230, 230);
      doc.roundedRect(margin + 80, yPos - 5, barWidth, barHeight, 1, 1, 'F');
      
      // Bar fill
      if (item.value > 0) {
        const fillWidth = (item.value / 100) * barWidth;
        doc.setFillColor(item.color[0], item.color[1], item.color[2], 0.7);
        doc.roundedRect(margin + 80, yPos - 5, fillWidth, barHeight, 1, 1, 'F');
      }
      
      // Percentage
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(60, 60, 60);
      doc.text(`${item.value.toFixed(1)}%`, pageWidth - margin - 40, yPos);
      
      yPos += barSpacing;
    });
    
    return yPos + 10; // Return the next Y position
  }

  /**
   * Add the recommendations section
   * @private
   */
  static _addRecommendations(doc, recommendations, startY) {
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    let yPos = startY;
    
    // Check if we need a new page
    if (yPos > doc.internal.pageSize.getHeight() - 80) {
      doc.addPage();
      yPos = 20;
    }
    
    // Section title
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(70, 70, 70);
    doc.text('RECOMMENDATIONS', margin, yPos);
    
    yPos += 8;
    
    // Create an emphasized box for recommendations
    doc.setFillColor(240, 248, 255); // Light blue background
    doc.setDrawColor(200, 220, 240); // Blue border
    doc.roundedRect(margin, yPos, pageWidth - (margin * 2), 10 + (recommendations.length * 7), 2, 2, 'FD');
    
    // Add recommendations as bullet points
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(60, 60, 60);
    
    recommendations.forEach((recommendation, index) => {
      doc.text(`• ${recommendation}`, margin + 10, yPos + 10 + (index * 7));
    });
    
    return yPos + 20 + (recommendations.length * 7); // Return the next Y position
  }

  /**
   * Add the footer to the report
   * @private
   */
  static _addFooter(doc, pageHeight) {
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    const footerY = pageHeight - 20;
    
    // Add line above footer
    doc.setDrawColor(220, 220, 220);
    doc.setLineWidth(0.5);
    doc.line(margin, footerY - 10, pageWidth - margin, footerY - 10);
    
    // Add disclaimer text
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(120, 120, 120);
    
    const disclaimer = "DISCLAIMER: This report is generated using artificial intelligence and is intended for informational purposes only. All findings should be reviewed and interpreted by a qualified healthcare professional. This analysis does not constitute a medical diagnosis or replace clinical judgment.";
    
    const splitDisclaimer = doc.splitTextToSize(disclaimer, pageWidth - (margin * 2));
    doc.text(splitDisclaimer, margin, footerY);
    
    // Add page number and website
    doc.setFont('helvetica', 'bold');
    doc.text(`Page ${doc.internal.getNumberOfPages()}`, pageWidth - margin, footerY, { align: 'right' });
    doc.text('www.medpredix.com', pageWidth / 2, footerY, { align: 'center' });
  }

  /**
   * Add a subtle watermark to the document
   * @private
   */
  static _addWatermark(doc, text) {
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    
    // Add watermark text
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(60);
    doc.setTextColor(150, 150, 150);
    
    // Set transparency
    doc.setGState(new doc.GState({ opacity: 0.1 }));
    
    // Properly position and rotate the watermark for current jsPDF version
    const textWidth = doc.getStringUnitWidth(text) * doc.getFontSize() / doc.internal.scaleFactor;
    
    // Apply rotation and positioning to the first page
    doc.saveGraphicsState();
    const x = pageWidth / 2;
    const y = pageHeight / 2;
    
    // For each page, add the rotated watermark
    const numPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= numPages; i++) {
      doc.setPage(i);
      
      // Save state and set opacity for each page
      doc.saveGraphicsState();
      doc.setGState(new doc.GState({ opacity: 0.1 }));
      
      // Manually calculate rotation and positioning
      doc.text(text, x, y, {
        align: 'center',
        angle: -45,
        rotationDirection: 0
      });
      
      doc.restoreGraphicsState();
    }
  }
}

export default ReportTemplate;